// papers.ts — the public papers register. Every label here is calibrated to
// peer-review status, not author confidence. The paper is the unit of
// commitment; the status is the truthful position relative to submission.
//
// Target journals are deliberately named at category level — "a
// commons-governance journal" rather than a specific outlet. Naming the
// outlet before submission prejudges editor reactions and invites
// embarrassment if the venue changes; naming the category signals
// seriousness without overcommitting.

export type PaperStatus =
  | "preprint"
  | "drafted"
  | "scaffolded"
  | "drafted-overbroad"
  | "hypothesis"
  | "conceptual"
  | "submitted"
  | "under-review";

export type PaperLayer = "L1" | "L2" | "L3" | "L4";

export type Paper = {
  id: string;
  /** Working manuscript title. */
  title: string;
  status: PaperStatus;
  /** Which framework layer this paper develops. */
  layer: PaperLayer;
  /** Single-sentence framing of the question this paper answers. */
  asks: string;
  /** "Theory paper" / "Empirical study" / "Position paper" / etc. */
  form: string;
  /** Optional non-obvious notes (target venue category, preprint plan, blocker). */
  notes?: string;
  /** Optional submission/target window. */
  window?: string;
  /** Optional cross-link back to the stage that owns this paper. */
  stageId?: string;
  /** DOI, once a preprint or publication exists. */
  doi?: string;
  /** Resolvable link for the DOI / preprint. */
  url?: string;
};

export const PAPERS: Paper[] = [
  {
    id: "P1",
    title:
      "Ostrom's Design Principles as Percolation Maintenance Specifications for Trust Networks: A Ninth Principle Predicted by Network Dynamics",
    status: "preprint",
    layer: "L3",
    asks: "Can Ostrom's eight design principles be read as maintenance operations on a trust network whose connectivity follows a percolation threshold, and does the mathematics predict a ninth?",
    form: "Theory paper with formal model and agent-based simulation",
    notes:
      "Published as a Zenodo preprint (v1.0, July 5, 2026) with DOI \u2014 the program's first formal publication of record. Journal submission ahead: primary venue a commons-governance journal, fallback a complex-systems journal.",
    window: "Preprint: July 2026 \u00b7 journal submission: 2026",
    stageId: "S1",
    doi: "10.5281/zenodo.21209148",
    url: "https://doi.org/10.5281/zenodo.21209148",
  },
  {
    id: "P2",
    title: "Reconciliation Dynamics",
    status: "scaffolded",
    layer: "L1",
    asks: "What are the conditions under which a relationship recovers from betrayal, and where do the bifurcations sit?",
    form: "Mathematical and simulation paper",
    notes:
      "Held narrow on purpose. Defines the betrayal accumulator, recovery term, and effective repair coefficient; reports phase portraits. Does not attempt empirical validation.",
    stageId: "S3",
  },
  {
    id: "P3",
    title: "The Trust Force Equation",
    status: "drafted-overbroad",
    layer: "L1",
    asks: "Can a relational trust score built from operational variables outperform existing computational trust models on shared cases?",
    form: "Theory paper plus scoring protocol",
    notes:
      "Blocked on operational scoring. Does not move until a coding manual is complete, external coders are trained, and inter-rater reliability is reported. Comparisons against Marsh, Jøsang, and FIRE are part of the submission package.",
    stageId: "S4",
  },
  {
    id: "P4",
    title: "Temporal Edge Persistence",
    status: "hypothesis",
    layer: "L3",
    asks: "Does periodic renewal predict edge persistence above a measurable threshold in a single bounded institutional domain?",
    form: "Empirical study",
    notes:
      "Pre-registered. One domain only — acequia, open source, Wikipedia, small-scale fisheries, or mutual aid. The result is reported whether or not it supports the hypothesis.",
    stageId: "S6",
  },
  {
    id: "P5",
    title: "AI Institutional Alignment",
    status: "conceptual",
    layer: "L4",
    asks: "What does the design-principle frame add to current accounts of AI alignment that the technical literature does not already supply?",
    form: "Position paper",
    notes:
      "Sequenced after Paper 1 is in review. Reads as institutional theory applied to AI, not AI commentary in search of a theory.",
    stageId: "S8",
  },
];
